import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackEvent, toGaItem } from '../lib/analyticsEvents';

const CartContext = createContext(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const savedCart = localStorage.getItem('legers-cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Erreur lors du chargement du panier:', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('legers-cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1, source = 'unknown') => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevItems, { ...product, quantity }];
    });

    trackEvent('add_to_cart', {
      currency: 'CHF',
      value: Number(product.price || 0) * Number(quantity || 1),
      source,
      items: [toGaItem(product, quantity)],
    });
  };

  const removeItem = (productId, source = 'unknown') => {
    const item = items.find((i) => i.id === productId);

    if (item) {
      trackEvent('remove_from_cart', {
        currency: 'CHF',
        value: Number(item.price || 0) * Number(item.quantity || 1),
        source,
        items: [toGaItem(item, item.quantity || 1)],
      });
    }

    setItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity, source = 'unknown') => {
    const item = items.find((i) => i.id === productId);
    if (!item) return;

    if (quantity <= 0) {
      removeItem(productId, source);
      return;
    }

    if (quantity > item.quantity) {
      trackEvent('add_to_cart', {
        currency: 'CHF',
        value: Number(item.price || 0) * Number(quantity - item.quantity),
        source,
        items: [toGaItem(item, quantity - item.quantity)],
      });
    }

    if (quantity < item.quantity) {
      trackEvent('remove_from_cart', {
        currency: 'CHF',
        value: Number(item.price || 0) * Number(item.quantity - quantity),
        source,
        items: [toGaItem(item, item.quantity - quantity)],
      });
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = (source = 'unknown') => {
    if (items.length) {
      trackEvent('clear_cart', {
        currency: 'CHF',
        value: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
        source,
        items: items.map((item) => toGaItem(item, item.quantity || 1)),
      });
    }

    setItems([]);
  };

  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
  const total = subtotal;

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen(prev => !prev);

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        subtotal,
        total,
        isOpen,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        openCart,
        closeCart,
        toggleCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;